import { Fab } from "@rmwc/fab";
import "@rmwc/fab/styles";
import { ListDivider, ListItem, ListItemGraphic, ListItemMeta, ListItemPrimaryText, ListItemSecondaryText, ListItemText } from "@rmwc/list";
import { MODES, SERVICES_TYPES, TARIF_TYPE, UNITE_FAMILLE, UNITE_TYPES } from "Achat/Constants";
import { showError } from "App/Toast/Toast";
import moment from "moment";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectDataReferentielUniteTypes } from "Referentiel/Store/selectors";
import { PersonneSelector } from "./PersonneSelector";
import { QuantiteSelector } from "./QuantiteSelector";
import { TimeSelector } from "./TimeSelector";
import { TrancheSelector } from "./TrancheSelector";
import { useGetServiceNameLibelle, useGetServiceTypeLibelle } from "./Utils/achatHook";
import { getNbUnitesTaxe, getValidQuantites, translateLibelleType } from "./Utils/achatUtils";

//#region CONSTANTES
const TYPE_ACHAT = {
  CHOIX: "choixTranche",
  MONTANT: "montant",
  PERSONNE: "personne",
  PRICE: "price",
  QUANTITE: "quantite",
  RACHAT: "rachat",
  SELECTED: "selected",
  TRANCHE: "idTranche",
}
// #endregion

// #region FONCTION COMPOSANT
/**
* Composant permettant d'afficher un service à acheter
* @class
* @category AchatListItem
*/
function AchatListItem(props) {
  // #region INITIALISATION
  const {
    serviceInCategorieSelected,
    isChildSelected,
    mode,
    onChange,
    openInfosTranches,
    service,
    serviceIndex,
    setMode,
    parentSelected = null
  } = props;


  // Initialisation du state
  const [canMajTax, setCanMajTax] = useState(true);
  //AYB 10.09.26 Type "4" : liste fermée au départ, panier ouvre sans sélectionner.
  const [choixListeVisible, setChoixListeVisible] = useState(false);

  // Initialisation du state de navigation
  const location = useLocation();
  
  // Initialisation du hook
  //AYB 2026.01.12 Affichage de la description du service en priorité pour l'ID site 1854, avec fallback sur le système de traduction actuel
  const serviceLibelle = useGetServiceNameLibelle(service.nom);
  const serviceTypeLibelle = useGetServiceTypeLibelle(service.idServiceType);
  const { t } = useTranslation();

  // Récupération de l'ID du site
  const idSite = location.state?.borne?.id;

  // Pour l'ID site 1854 : si description = "STATIONNEMENT EAU ET ELECTRICITE INCLUS..." ou "STATIONNEMENT" seul : traduire ; sinon garder la description
  // Hors 1854 : libellé traduit (type pour accès temporaire et EAU_ELEC, sinon nom)
  //AYB 26.08.25 affichage traduit du type EAU_ELEC (id 8) via services_types.eau_elec
  const translatedLabel = (service.idServiceType === SERVICES_TYPES.ACCES_TEMPORAIRE || service.idServiceType === SERVICES_TYPES.EAU_ELEC)
    ? (serviceTypeLibelle || serviceLibelle)
    : serviceLibelle;
  const desc = service.description?.trim();
  const descNorm = desc ? desc.toUpperCase().replace(/\s+/g, " ") : "";
  const isStationnementOnly = descNorm === "STATIONNEMENT";
  const isStationnementEauElecDesc = descNorm.includes("EAU ET ELECTRICITE INCLUS") && (descNorm.includes("STATIONNEMENT") || descNorm.includes("STAIONNEMENT")) && descNorm.includes("TAXE") && descNorm.includes("0.58");
  const useTranslatedFor1854 = isStationnementOnly || isStationnementEauElecDesc;
  const serviceLib = (idSite === 1854)
    ? (desc && !useTranslatedFor1854 ? desc : (isStationnementEauElecDesc ? t("achat.descriptionStationnementEauElec") : translatedLabel))
    : translatedLabel;

  // Initialisation des références
  // AYB 2026.01.26 - Récupération sécurisée des services actifs pour détecter les services déjà achetés lors d'un achat normal (évite les doublons)
  const servicesActifs = location.state?.servicesActifs || [];
  const isActif = useRef(Array.isArray(servicesActifs) && servicesActifs.some(sa => sa.idService === service.idService));
  const hasPlaces = useRef(service.places && service.places > 0); // calcul du nombre de places disponible

  // Initialisation des sélecteurs
  const uniteTypes = useSelector((state) => selectDataReferentielUniteTypes(state));
  // #endregion

  // #region UTILS
  // Renvoie le tarif a afficher
  const getCurrentTarif = useCallback((value) => {

    // Si une seule tranche ou pas de quantité
    if ((service.tarif && service.tarif.tranches.length === 1)) {
      return service.tarif.tranches[0];
    }

    // Sinon recherche de la bonne tranche de tarif
    // On récupère la date de validité avec la valeur
    const dateValidite = (service.tarif.idTarifType === TARIF_TYPE.TEMPS && value) > 1
      ? moment().add(value * service.tarif.secondes, "second").format()
      : moment().format()


    // On filtre les tarifs qui sont dans la date / période de validité
    let tranchesFilter = [];
    tranchesFilter = service.tarif.tranches.filter(item => {
      // Recherche des tranches entre la date de début et fin

      if (moment(dateValidite).isBetween(item.dateDebut, item.dateFin)) {
        const periodeDeb = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeDebut, 'DD/MM/YYYY HH:mm').format();
        const periodeFin = moment(moment(dateValidite).format("DD/MM/YYYY") + ' ' + item.periodeFin, 'DD/MM/YYYY HH:mm').format();

        // Recherche dans la période
        return moment(dateValidite).isBetween(periodeDeb, periodeFin);
      }

      return false;
    });

    // on renvoie le bon item dans la liste
    let tranche = tranchesFilter.length === 1 || !value
      ? tranchesFilter[0]
      : tranchesFilter.find(item => (value <= item.uniteMax) && (value >= item.uniteMin))

    // Fallback uniquement si la quantité dépasse le max de toutes les tranches
    if (!tranche && value && tranchesFilter.length > 0) {
      const maxUnite = Math.max(...tranchesFilter.map(t => t.uniteMax));
      if (value > maxUnite) {
        const tranchesSorted = [...tranchesFilter].sort((a, b) => b.uniteMax - a.uniteMax);
        tranche = tranchesSorted[0];
      }
    }

    return tranche;
  }, [service]);

  // AYB 2026.02.17 - Traduit le libellé d'unité (heures, minutes, Emplacement 24H, etc.) selon la langue
  const getTranslatedLibelleType = () => {
    return translateLibelleType(service.tarif?.libelleType, t, service.tarif?.idUniteType);
  };

  // Renvoie la quantité limitée qui peut être achetée pour un service
  const getLimit = (quantiteMax = service.quantiteLimite) => {
    const libelleType = service.tarif?.libelleType || "";
    let uniteLabel = libelleType.split(" ").slice(-1);
    //AYB 2026.03.27 - Récupération du type d'unité pour afficher le libellé correct
    const uniteType = uniteTypes.data.find(type => type.idUniteType === service.tarif.idUniteType);
    const isFamilleTemps = uniteType?.famille === UNITE_FAMILLE.TEMPS || String(service.tarif?.idTarifType) === TARIF_TYPE.TEMPS;
    //AYB 2026.03.27 - Si le service est de type temps, on affiche la durée totale en secondes
    if (isFamilleTemps) {
      // Calcul de la durée totale en secondes
      const totalSecondes = quantiteMax * service.tarif.secondes;

      // Conversion en minutes
      const totalMinutes = totalSecondes / 60;

      // Conversion en heures
      const totalHeures = totalMinutes / 60;

      let timeLimit;

      if (totalHeures < 1) {
        // Affichage en minutes (moins de 1 heure)
        timeLimit = totalMinutes;
        uniteLabel = " " + (totalMinutes > 1 ? t("achat.minutes") : t("achat.minute"));
      } else if (totalHeures < 24) {
        // Affichage en heures (entre 1 et 24 heures)
        timeLimit = totalHeures;
        uniteLabel = " " + (totalHeures > 1 ? t("achat.hours") : t("achat.hour"));
      } else {
        // Affichage en jours (24 heures et plus)
        timeLimit = totalHeures / 24;
        uniteLabel = " " + (timeLimit > 1 ? t("achat.days") : t("achat.day"));
      }

      // Arrondir à 2 décimales si nécessaire
      timeLimit = Math.round(timeLimit * 100) / 100;

      return `${timeLimit}${uniteLabel}`;
    } else {
      // Limite en quantité (unité traduite pour Emplacement 24H, etc.)
      const rawLabel = (Array.isArray(uniteLabel) ? uniteLabel[0] : uniteLabel) || "";
      const label = (service.tarif.idUniteType === UNITE_TYPES.EMPLACEMENT_24H)
        ? t("achat.emplacement24h")
        : rawLabel;
      return `${quantiteMax} ${label}`;
    }
  };

  //AYB 08.09.26 Toast si on tente de dépasser uniteMax (même avec une seule tranche).
  const handleMaxReached = (quantiteMax) => {
    const valid = getValidQuantites(service.tarif);
    const maxQ = quantiteMax ?? valid[valid.length - 1];
    if (!maxQ) return;
    showError(t("achat.serviceLimitMax", { limit: getLimit(maxQ) }));
  };

  // récupère le nouveau montant lors du changement de quantité
  const getMontant = (newService) => {
    let result = service.montant ?? 0;
    const actualQte = service.quantite ?? 0;
    const newQte = newService.quantite;
    const actualPrice = service.actualTranche?.prix ?? 0;
    const newPrice = newService.actualTranche?.prix ?? 0;

    if (!newService.actualTranche && newQte > 0) return result; // quantité non définie dans une tranche : ne pas compter
    if (!service.actualTranche && actualQte > 0) return (newQte ? newPrice : 0);

    if (newQte > actualQte) {
      result = result + newPrice;
    } else if (newQte < actualQte) {
      result = result - actualPrice;
    }

    // if (parentSelected && (newService.tarif.idUniteType === UNITE_TYPES.EMPLACEMENT_24H) && actualQte !== parentSelected.quantite) {
    //   result = result * newQte;
    // }
    //AYB 10.09.26 Alignement emplacement sur le ratio de durées, plus sur parent.quantite seul.
    if (parentSelected && (newService.tarif.idUniteType === UNITE_TYPES.EMPLACEMENT_24H) && actualQte !== getNbUnitesTaxe(parentSelected, newService.tarif)) {
      result = result * newQte;
    }

    return result;
  };

  // Renvoie si le bouton + pour acheter doit être visible ou non
  const isCartInterfaceVisible = () => {
    let result = true;

    // Un seul service par catégorie (type) en achat ; en rachat, un service actif ne bloque pas les autres du même type
    if (!parentSelected && serviceInCategorieSelected && (serviceInCategorieSelected.idService !== service.idService) && !serviceInCategorieSelected.rachat && !service.rachat) {
      return false;
    }

    // Si le service parent est obligatoire, il faut au moins une unité achetée pour pouvoir acheter le service enfant
    if (parentSelected && parentSelected.serviceObligatoire && !parentSelected.quantite && !parentSelected.rachat) {
      return false;
    }

    //AYB 14.09.26 Tarif pas encore rattaché (chargement site) : ne pas lire idUniteType.
    if (service.tarif?.idUniteType === UNITE_TYPES.PERSONNE_24H && !parentSelected?.quantite && !parentSelected?.rachat) {
      return false;
    }

    return result;
  };
  // #endregion

  // #region EVENTS
  // Modification du state
  const handleChange = (name, value) => {
    let d = { ...service };
    let newPrice = 0;
    let newQuantite = 0;

    //AYB 08.09.26 Type "4" : sélection d'une ligne de la liste (un tarif), quantité et tranche de ce tarif.
    if (name === TYPE_ACHAT.CHOIX) {
      const quantite = value?.quantite;
      if (!quantite) {
        if (isChildSelected && d.idServiceType !== SERVICES_TYPES.PARKING) {
          showError("Un service enfant est sélectionné, vous devez d'abord le supprimer");
          return;
        }
        d[TYPE_ACHAT.SELECTED] = false;
        d[TYPE_ACHAT.QUANTITE] = 0;
        d[TYPE_ACHAT.MONTANT] = 0;
        setChoixListeVisible(true);
        onChange(d, serviceIndex);
        return;
      }
      if (!value?.actualTranche) return;
      //AYB 14.09.26 Type "4" : bloquer un 2e service du même type (EAU 1 + EAU 2) ; le bouton panier ne suffisait pas, la liste restait cliquable.
      if (!parentSelected && serviceInCategorieSelected && !serviceInCategorieSelected.rachat && !d.rachat) {
        return;
      }
      d.tarif = value.tarif ?? d.tarif;
      d.actualTranche = value.actualTranche;
      d[TYPE_ACHAT.SELECTED] = true;
      d[TYPE_ACHAT.QUANTITE] = quantite;
      d[TYPE_ACHAT.MONTANT] = Math.round(quantite * (value.actualTranche.prix ?? 0) * 100) / 100;
      d[TYPE_ACHAT.TRANCHE] = value.actualTranche.idTranche;
      onChange(d, serviceIndex);
      return;
    }

    const uniteType = uniteTypes.data.find(type => type.idUniteType === d.tarif.idUniteType);
    const isFamilleTemps = uniteType?.famille === UNITE_FAMILLE.TEMPS || String(d.tarif?.idTarifType) === TARIF_TYPE.TEMPS;
    const quantiteDemandee = isFamilleTemps ? (value / d.tarif.secondes) : value;

    // Si la valeure excède la limite du service, on renvoie un message d'erreur
    if (d.limite && (quantiteDemandee > d.quantiteLimite)) {
      showError(t("achat.serviceLimitMax", { limit: getLimit() }));
      return;
    }

    // Si pas de valeur, on déselectionne le service
    if (!value) {
      if (isChildSelected && d.idServiceType !== SERVICES_TYPES.PARKING) {
        showError("Un service enfant est sélectionné, vous devez d'abord le supprimer");
        return;
      }

      d[TYPE_ACHAT.SELECTED] = false;
      d[TYPE_ACHAT.QUANTITE] = 0;
      d[TYPE_ACHAT.MONTANT] = 0;
      onChange(d, serviceIndex);
      return;
    }

    switch (name) {
      case TYPE_ACHAT.PRICE:
        newQuantite = Math.round(value / d.tarif.secondes);
        d.actualTranche = getCurrentTarif(newQuantite);
        d[TYPE_ACHAT.QUANTITE] = newQuantite;
        d[TYPE_ACHAT.MONTANT] = Math.round(getMontant(d) * 100) / 100;
        break;

      case TYPE_ACHAT.PERSONNE:
      case TYPE_ACHAT.QUANTITE:
        d.actualTranche = getCurrentTarif(value);
        d[TYPE_ACHAT.SELECTED] = true;
        d[TYPE_ACHAT.QUANTITE] = value;
        d[TYPE_ACHAT.MONTANT] = Math.round(getMontant(d) * 100) / 100;
        break;

      case TYPE_ACHAT.SELECTED:
        //AYB 08.09.26 Type "4" : 1re tranche (filtre date), quantité = uniteMin, prix de cette tranche.
        if (String(d.tarif?.idTarifType) === TARIF_TYPE.CHOIX) {
          const first = d.tranchesChoix?.[0];
          const firstTranche = first?.tranches?.[0];
          if (!firstTranche) return;
          const qty = firstTranche.uniteMin ?? 1;
          d.tarif = first.tarif ?? d.tarif;
          d.actualTranche = firstTranche;
          d[TYPE_ACHAT.SELECTED] = true;
          d[TYPE_ACHAT.QUANTITE] = qty;
          d[TYPE_ACHAT.MONTANT] = Math.round(qty * (firstTranche.prix ?? 0) * 100) / 100;
          break;
        }
        d.actualTranche = getCurrentTarif(value);
        d[TYPE_ACHAT.SELECTED] = true;
        d[TYPE_ACHAT.MONTANT] = d.actualTranche?.prix ?? 0;
        d[TYPE_ACHAT.QUANTITE] = value;
        break;

      default:
        break;
    }

    // Ne pas appliquer la modification si la quantité n'est dans aucune tranche
    if (value && !d.actualTranche) return;

    if (service.tarif.idUniteType === UNITE_TYPES.PERSONNE_24H && d.actualTranche) {
      // newPrice = (d.actualTranche.prix * parentSelected.quantite) * value;
      //AYB 10.09.26 Ratio durées parent/taxe (ex. 48h / 24h = 2), plus seulement parent.quantite.
      const nbUnitesTaxe = getNbUnitesTaxe(parentSelected, d.tarif);
      newPrice = (d.actualTranche.prix * nbUnitesTaxe) * value;
      d[TYPE_ACHAT.MONTANT] = Math.round(newPrice * 100) / 100;
    }

    d[TYPE_ACHAT.TRANCHE] = d.actualTranche?.idTranche;

    onChange(d, serviceIndex);
  };

  // Vérification de la disponibilité en cas de rachat
  const handleDisponibilite = () => {
    // Si le service est marqué comme rachat, il doit être disponible
    if (service.rachat || isActif.current) {
      return true;
    } else if (!service.disponible) {
      if (service.idServiceType === SERVICES_TYPES.PARKING) {
        return hasPlaces.current;
      }
      return true;
    }
    return false;
  };
  // #endregion

  // #region HOOK D'EFFET
  useEffect(() => {
    // si dépassement de stationnement, initialisation
    if (!service.depassement || service.depassement < 1) {
      return;
    }

    handleChange(TYPE_ACHAT.SELECTED, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gestion des services de "type taxe de séjour"
  useEffect(() => {
    if (!service.tarif) return;
    //AYB 08.09.26 Type "4" : ne pas écraser la tranche choisie via getCurrentTarif(quantite).
    if (String(service.tarif.idTarifType) !== TARIF_TYPE.CHOIX) {
      service.actualTranche = getCurrentTarif(service.quantite);
    }

    if ((service.idServiceType === SERVICES_TYPES.TAXE_SEJOUR)) {
      if (parentSelected && !service.quantite) {
        setCanMajTax(true);
      }

      // Personne : le service commence avec une quantité de 2 personnes
      if (service.tarif.idUniteType === UNITE_TYPES.PERSONNE_24H) {
        if (!parentSelected.quantite) {
          return;
        }

        if (parentSelected && canMajTax) {
          handleChange(TYPE_ACHAT.QUANTITE, service.quantite ? service.quantite : 2);
          setCanMajTax(false);
          return;
        }
      }

      // Emplacement :  quantité identique au parent
      if ((service.tarif.idUniteType === UNITE_TYPES.EMPLACEMENT_24H) && parentSelected && canMajTax) {
        setCanMajTax(false);
        // handleChange(TYPE_ACHAT.QUANTITE, parentSelected.quantite);
        //AYB 10.09.26 Qté emplacement = (qté parent × secondes parent) / secondes taxe (48h → 2), plus une copie de parent.quantite.
        handleChange(TYPE_ACHAT.QUANTITE, getNbUnitesTaxe(parentSelected, service.tarif));
        return;
      }

      // Si pas de parent, on vide la taxe de séjour
      if (!parentSelected && service.quantite) {
        handleChange(null, 0);
        setMode(MODES.PARENT);
        setCanMajTax(true);
        showError(t("achat.selectService"));
        return;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCurrentTarif, service]);

  //AYB 14.09.26 Type "4" : fermer la liste si un autre service du même type vient d'être choisi (rachat : un actif ne bloque pas).
  useEffect(() => {
    if (!service.tarif) {
      return;
    }
    if (!service.selected && !isCartInterfaceVisible()) {
      setChoixListeVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.selected, service.tarif, serviceInCategorieSelected]);
  // #endregion

  // #region INTERFACE
  // Affiche le bouton pour acheter un service
  const cartInterface = () => {
    return isCartInterfaceVisible()
      ?
      handleDisponibilite()
        ?
        <Fab
          icon="add_shopping_cart"
          mini
          onClick={() => {
            setChoixListeVisible(true);
            //AYB 10.09.26 Type "4" : ouvrir la liste sans sélectionner le 1er tarif.
            if (String(service.tarif?.idTarifType) !== TARIF_TYPE.CHOIX) {
              handleChange(TYPE_ACHAT.SELECTED, 1);
            }
          }}
        />
        :
        <div>{t("achat.serviceOut")}</div>
      :
      null
  };

  // Affiche le sélecteur pour ajouter / diminuer la quantité de service sélectionné
  const timeOrQuantiteSelector = () => {
    //AYB 08.09.26 Type "4" : le panier devient une flèche (bas = afficher, haut = masquer). Types "1"/"2" : TimeSelector / QuantiteSelector.
    if (String(service.tarif?.idTarifType) === TARIF_TYPE.CHOIX) {
      if (mode === MODES.TOTAL) return null;
      const tarifChoisiLibelle = translateLibelleType(service.tarif?.libelleType, t, service.tarif?.idUniteType);
      return (
        <div className="Achat-ChoixMeta">
          {!choixListeVisible && service.selected && tarifChoisiLibelle &&
            <span className="Achat-ChoixSelected">{tarifChoisiLibelle}</span>
          }
          <Fab
            icon="expand_more"
            mini
            style={{
              transform: choixListeVisible ? "rotate(180deg)" : "none",
              transition: "transform 0.2s ease",
            }}
            onClick={(event) => {
              event.stopPropagation();
              setChoixListeVisible((visible) => !visible);
            }}
          />
        </div>
      );
    }

    switch (service.tarif.idUniteType) {
      case UNITE_TYPES.PERSONNE_24H:
        return (
          <PersonneSelector
            doShowButtons={service.selected && mode !== MODES.TOTAL}
            onChange={(value) => { handleChange(TYPE_ACHAT.PERSONNE, value) }}
            value={service.quantite}
          />
        )


      default:
        const uniteType = uniteTypes.data.find(type => type.idUniteType === service.tarif.idUniteType);
        const isTarifTemps = String(service.tarif.idTarifType) === TARIF_TYPE.TEMPS || uniteType?.famille === UNITE_FAMILLE.TEMPS;
        if (isTarifTemps) {
          //AYB 08.09.26 Appliquer uniteMin/uniteMax même s'il n'y a qu'une tranche (ex. Eau 1–10).
          const validQuantitesTime = service.tarif.tranches?.length
            ? getValidQuantites(service.tarif)
            : undefined;
          return (
            <TimeSelector
              depassement={service.depassement * service.tarif.secondes}
              doShowButtons={service.selected && mode !== MODES.TOTAL && (service.tarif.idUniteType !== UNITE_TYPES.EMPLACEMENT_24H)}
              mode={mode}
              onChange={(value) => { handleChange(TYPE_ACHAT.PRICE, value) }}
              onMaxReached={handleMaxReached}
              step={service.tarif.secondes}
              validQuantites={validQuantitesTime}
              value={service.quantite * service.tarif.secondes}
            />
          )
        }

        const validQuantites = service.tarif.tranches?.length
          ? getValidQuantites(service.tarif)
          : undefined;
        return (
          <QuantiteSelector
            doShowButtons={service.selected && mode !== MODES.TOTAL}
            onChange={(value) => { handleChange(TYPE_ACHAT.QUANTITE, value) }}
            onMaxReached={handleMaxReached}
            validQuantites={validQuantites}
            value={service.quantite}
          />
        )
    }
  };

  return (
    <>
      <ListItem
        key={service.idService}
        className="Achat-ServiceItem"
        ripple={false}
        style={{
          width: "100%",
          boxSizing: "border-box",
          minHeight: "100px",
          height: "auto",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          display: "flex",
          alignItems: "flex-start",
          overflow: "visible",
        }}
      >
        <ListItemText style={{ flex: 1, minWidth: 0, overflow: 'visible', paddingRight: '8px', display: 'flex', flexDirection: 'column' }}>
          <ListItemPrimaryText style={{ wordBreak: 'break-word', whiteSpace: 'normal', overflow: 'visible', lineHeight: '1.4', display: 'block' }}>
            {serviceLib}
          </ListItemPrimaryText>
          {service.actualTranche && service.tarif &&
            <ListItemSecondaryText style={{ display: 'block' }}>
              {`${service.actualTranche.prix} ${service.tarif.symbole} / ${getTranslatedLibelleType()}`}
            </ListItemSecondaryText>
          }
          {service.limite && service.tarif ?
            <ListItemSecondaryText style={{ display: 'block' }}>
              {t("achat.serviceLimit") + getLimit()}
            </ListItemSecondaryText>
            :
            null
          }
          {service.places && service.places > 0 &&
            <ListItemSecondaryText style={{ display: 'block' }}>
              {service.places} {t("achat.places")}
            </ListItemSecondaryText>
          }
        </ListItemText>
        <ListItemMeta style={{ flexShrink: 0, alignSelf: 'flex-start', marginTop: '4px' }}>
          {/* AYB 08.09.26 Type "4" : pas d'icône info, le détail est la liste sous la ligne. */}
          {String(service.tarif?.idTarifType) !== TARIF_TYPE.CHOIX && service.tarif && service.tarif.tranches && service.tarif.tranches.length > 1
            ?
            <ListItemGraphic
              icon="info"
              onClick={() => openInfosTranches(service)}
            />
            :
            null
          }
          {/* AYB 14.09.26 Type "4" : flèche/liste seulement si ce service est choisi, ou si aucun autre du même type n'est au panier. */}
          {String(service.tarif?.idTarifType) === TARIF_TYPE.CHOIX && (service.selected || (choixListeVisible && isCartInterfaceVisible()))
            ? timeOrQuantiteSelector()
            : service.selected ? (service.tarif ? timeOrQuantiteSelector() : null) : (service.tarif ? cartInterface() : null)}
        </ListItemMeta>
      </ListItem>
      {/* AYB 10.09.26 Type "4" : liste visible dès le panier, même sans tarif choisi. */}
      {/* AYB 14.09.26 Type "4" : masquer la liste si un autre service du même type est déjà sélectionné. */}
      {String(service.tarif?.idTarifType) === TARIF_TYPE.CHOIX && (service.selected || isCartInterfaceVisible()) && (choixListeVisible || (service.selected && mode === MODES.TOTAL)) && (
        <TrancheSelector
          clickable={mode !== MODES.TOTAL}
          onChange={(option) => handleChange(TYPE_ACHAT.CHOIX, option)}
          onMaxReached={handleMaxReached}
          selectedIdTarif={service.selected && service.quantite ? service.tarif?.idTarif : null}
          selectedQuantite={service.selected ? service.quantite : 0}
          symbole={service.tarif.symbole}
          tarifsChoix={service.tranchesChoix || []}
        />
      )}
      <ListDivider />
    </>
  );
  // #endregion
};
// #endregion

// #region PROPRIETES
/**
* Type des propriétés de {@link AchatListItem}
* @typedef {Object} AchatListItem.propTypes
* @property {object} number Mode de l'écran
* @property {object} parentSelected Service parent sélectionné
* @property {object} service L'item du service
* @property {number} serviceIndex L'index du service
*/
AchatListItem.propTypes = {
  mode: PropTypes.number.isRequired,
  parentSelected: PropTypes.object,
  service: PropTypes.object.isRequired,
  serviceIndex: PropTypes.number.isRequired
};
// #endregion

export { AchatListItem };

